// src/components/fabricanteDetailForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import api from './api'; // Asegúrate de tener esta instancia configurada con el token

const initialFormData = {
  id: null,
  name: '',
  tecnologia: '',
  status: false,
  assignedPlants: [],
  availablePlants: [],
  creationDate: '',
  updateDate: '',
};

function FabricanteDetail({ open, onClose, fabricante, onSave }) {
  const [formData, setFormData] = useState(initialFormData);

  const [checkedAvailable, setCheckedAvailable] = useState([]);
  const [checkedAssigned, setCheckedAssigned] = useState([]);

  const [errors, setErrors] = useState({}); //estado para los errores
  //Nuevo estado para determinar si estamos en modo de creación 
  const isCreating = formData.id === null;

  //campos obligatorios
  const requiredFields = [
    'name',
    'tecnologia',
  ];


  useEffect(() => {
    // Si el modal se está abriendo (open es true)
    if (open) {
      if (fabricante) { // Modo edición: Cargar datos del fabricante existente
        setFormData({
          id: fabricante.id,
          name: fabricante.nombre || '',
          tecnologia: fabricante.tecnologia || '',
          status: fabricante.is_active || false,
          assignedPlants: fabricante.plantas || [],
          availablePlants: [], // No se usan si no hay selector, o se cargarían desde la API
          creationDate: fabricante.created_at || '',
          updateDate: fabricante.updated_at || '',
        });
        // Cargar availablePlants si el modo edición lo requiere para la selección.
        // api.get('/plantas/?skip=0&limit=100').then(res => { /* ... lógica ... */ });

      } else { // Modo creación: Reiniciar a los valores iniciales
        setFormData(initialFormData); 
      }
      // Siempre limpia estos estados cuando el modal se abre (o re-abre)
      setCheckedAssigned([]);
      setCheckedAvailable([]);
      setErrors({}); // Limpia los errores al abrir el modal
    }
  }, [open, fabricante]); 

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    // Limpiar el error si el usuario empieza a escribir/seleccionar en el campo
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: false,
      }));
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    const newErrors = {};
    let isValid = true;

    requiredFields.forEach(field => {
      const value = formData[field];
      // Para TextFields (cadenas): verifica si está vacío o solo con espacios
      if (typeof value === 'string' && value.trim() === '') {
        newErrors[field] = true;
        isValid = false;
      }
      // Para Selects (pueden tener valores numéricos o strings): verifica si es null, undefined, 0 o cadena vacía
      else if (value === null || value === undefined || value === '' || value === 0) {
        newErrors[field] = true;
        isValid = false;
      }

      else if (Array.isArray(value) && value.length === 0) {
        newErrors[field] = true;
        isValid = false;
      }
    });

    setErrors(newErrors); // Actualiza el estado de errores para re-renderizar

    if (!isValid) {
      console.error('Campos obligatorios vacíos:', newErrors);
      return; // ¡IMPORTANTE! Detiene la ejecución aquí si hay errores de validación
    }

    // Si la validación pasa, procede con el guardado

    const payload = {
      nombre: formData.name,
      tecnologia: formData.tecnologia,
      is_active: formData.status,
      ...(isCreating ? {} : { plantas: formData.assignedPlants }),
    };

    try {
      if (formData.id) {
        await api.put(`/fabricante/${formData.id}`, payload);
      } else {
        await api.post('/fabricante/', payload);
      }
      onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error al guardar fabricante:', error);
      alert('Ocurrió un error al guardar el fabricante.');
    }
  };

  const handleCancel = () => {
    setErrors({});
    onClose();
  };



  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Detalle de Fabricante</Typography>
          <Typography variant="body2" color="text.secondary">Usuarios &gt; Detalle</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Nombre" name="name" value={formData.name} onChange={handleChange} variant="outlined" size="small"
             required error={!!errors.name}
             helperText={errors.name ? 'Este campo es obligatorio' : ''}/>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Tecnología" name="tecnologia" value={formData.tecnologia} onChange={handleChange} variant="outlined" size="small"
            required error={!!errors.tecnologia}
            helperText={errors.tecnologia ? 'Este campo es obligatorio' : ''} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={<Switch checked={formData.status} onChange={handleChange} name="status" color="primary" />}
              label="Estatus"
              labelPlacement="start"
              sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
            />
          </Grid>

          {!isCreating && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle1" gutterBottom>Plantas</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 5 }}>
                  <List dense sx={{ width: '100%', height: 200, overflow: 'auto', border: '1px solid #ccc' }}>
                    {formData.availablePlants.map((plant, index) => (
                      <ListItem key={plant} button>
                        <ListItemText primary={plant} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Grid>
          )}

          {!isCreating && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Fecha de Creación" name="creationDate" value={formData.creationDate} onChange={handleChange} variant="outlined" size="small" disabled />
            </Grid>)}
          {!isCreating && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Fecha actualización" name="updateDate" value={formData.updateDate} onChange={handleChange} variant="outlined" size="small" disabled />
            </Grid>)}

        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={handleCancel}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default FabricanteDetail;

